/**
 * EligibilityBadge — a worker's or contractor's work-eligibility, with the reason.
 *
 * "Worker is not eligible" generates a support ticket. This never shows that:
 * the chip expands to the specific blockers, each with what is wrong and the
 * action that fixes it, so the person looking at it can resolve it themselves.
 *
 * Three states, because "eligible but something lapses on Friday" is the state
 * a supervisor most needs to see and the one a boolean hides:
 *
 *   blocked   — at least one blocker; work must not proceed
 *   expiring  — eligible now, something lapses within the warning window
 *   eligible  — nothing outstanding
 *
 * Takes an already-fetched result rather than fetching its own, so a list of a
 * hundred workers is one bulk request instead of a hundred.
 */
import React, { useState } from 'react';
import {
  CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronRight, Loader2,
} from 'lucide-react';

const STYLES = {
  eligible: {
    chip: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
    Icon: CheckCircle2, label: 'Eligible',
  },
  expiring: {
    chip: 'bg-amber-50 text-amber-800 ring-amber-200',
    Icon: AlertTriangle, label: 'Expiring soon',
  },
  blocked: {
    chip: 'bg-red-50 text-red-800 ring-red-200',
    Icon: XCircle, label: 'Not eligible',
  },
  unknown: {
    chip: 'bg-gray-100 text-gray-600 ring-gray-200',
    Icon: AlertTriangle, label: 'Not verified',
  },
};

export function eligibilityState(result) {
  if (!result) return 'unknown';
  if (!result.eligible) return 'blocked';
  if ((result.expiring_soon || []).length > 0) return 'expiring';
  return 'eligible';
}

export default function EligibilityBadge({
  result,
  loading = false,
  compact = false,
  // When enforcement is advisory, say so — otherwise a red chip implies work
  // will be stopped when it will not be.
  advisory = false,
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px]
                       font-medium text-gray-500 ring-1 ring-gray-200">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking…
      </span>
    );
  }

  const state = eligibilityState(result);
  const { chip, Icon, label } = STYLES[state];
  const blockers = result?.blockers || [];
  const expiring = result?.expiring_soon || [];
  const satisfied = result?.satisfied || [];
  const hasDetail = blockers.length > 0 || expiring.length > 0;

  const summary = state === 'blocked' && blockers.length
    ? `${label} — ${blockers.length} issue${blockers.length === 1 ? '' : 's'}`
    : state === 'expiring'
      ? `${expiring.length} expiring`
      : label;

  return (
    <div className={compact ? 'inline-block' : ''}>
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px]
                    font-medium ring-1 ${chip} ${hasDetail ? 'cursor-pointer' : 'cursor-default'}`}
        title={hasDetail ? 'Show why' : undefined}
      >
        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
        {summary}
        {advisory && state === 'blocked' && (
          <span className="font-normal opacity-75">(advisory)</span>
        )}
        {hasDetail && (open
          ? <ChevronDown className="h-3 w-3" />
          : <ChevronRight className="h-3 w-3" />)}
      </button>

      {open && hasDetail && (
        <div className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-white p-3">
          {result?.worker_name && (
            <p className="text-xs font-semibold text-gray-900">
              {result.worker_name}
              {result.contractor?.name && (
                <span className="font-normal text-gray-500">
                  {' · '}{result.contractor.name}
                </span>
              )}
            </p>
          )}

          {blockers.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-red-700">
                Not eligible because
              </p>
              <ul className="mt-1 space-y-1.5">
                {blockers.map((b) => (
                  <li key={b.code} className="flex items-start gap-1.5">
                    <XCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-red-500" />
                    <span className="min-w-0">
                      <span className="text-xs text-gray-900">{b.label}</span>
                      {b.detail && (
                        <span className="block text-[11px] text-gray-500">{b.detail}</span>
                      )}
                      {b.action && (
                        <span className="block text-[11px] font-medium text-blue-700">
                          → {b.action}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {expiring.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                Expiring soon
              </p>
              <ul className="mt-1 space-y-1">
                {expiring.map((e) => (
                  <li key={e.code} className="flex items-start gap-1.5">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                    <span className="text-xs text-gray-900">
                      {e.label}
                      {e.expires_on && (
                        <span className="text-gray-500">{' · '}{e.expires_on}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {satisfied.length > 0 && (
            <details className="text-[11px] text-gray-500">
              <summary className="cursor-pointer">
                {satisfied.length} requirement{satisfied.length === 1 ? '' : 's'} met
              </summary>
              <ul className="mt-1 space-y-0.5">
                {satisfied.map((sat) => (
                  <li key={sat.code} className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-emerald-500" />
                    {sat.label}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
